import { useRouterState } from "@tanstack/react-router";
import { lazy, type ReactNode, Suspense, useLayoutEffect } from "react";
import { SiteFooter } from "@/components/SiteFooter";
import { StageNav } from "@/components/StageNav";
import { AnalyticsNotice } from "@/features/analytics/components/AnalyticsNotice";
import { StageSurround } from "@/features/viewer/components/StageSurround";
import { preloadStageHost } from "@/features/viewer/preloadStageHost";
import { isStageHostRoute } from "@/lib/screenTransitions";
import { THEME_COLOR, useThemeColor } from "@/lib/useThemeColor";

// The persistent Stage canvas is code-split off the entry: all of
// three/drei/postprocessing lives behind this dynamic import, so the conversion
// landing `/` (and every non-host route) pulls in ZERO three.js (ADR-0040 §4,
// ADR-0041 §7). It loads only when a host route (/upload, /preview) renders it.
// StageSurround is the warm gradient SHOWN while that chunk downloads (it carries
// no three, so it stays in the entry bundle) — otherwise the cold-load window
// flashed the white page through the transparent <main> (the /upload white-screen).
//
// `lazy` calls the SAME memoized preloader the `/upload` CTAs warm on intent (#346),
// so a hover-preloaded chunk and this mount resolve to ONE fetch — and if intent
// already resolved it, StageHost mounts with no Suspense fallback (no "pop").
const StageHost = lazy(preloadStageHost);

// Routes whose viewer should fill the ENTIRE screen — an edge-to-edge, no-scroll
// single-page surface (mobile included): the 3D Stage is the product, not a
// widget. They drop the page chrome (footer/max-width) and lock to the dynamic
// viewport height so there's no page scroll — but the nav stays as a FLOATING bar
// over the viewer. (Still wrapped in <main> so the route keeps its landmark.)
const FULLSCREEN_PREFIXES = ["/upload", "/stage-lab", "/preview"];

// Routes backed by the ONE persistent Stage canvas (StageHost): the front-door
// studio + the preview. On these, <main> is transparent and pointer-transparent
// so the fixed canvas behind it shows through and orbits, while the route's own
// chrome opts back into pointer events. (/stage-lab is fullscreen but mounts its
// own canvas, so it is NOT a host route.) The predicate is shared with the router's
// Screen-transition config (ADR-0052) — the same host routes it keeps transition-free.

// Routes that render with ZERO chrome — not even the floating nav. The dev-only
// /shot screenshot harness (#92) needs a stripped surface: just the Stage canvas,
// nothing the deterministic screenshot shouldn't contain.
const BARE_PREFIXES = ["/shot"];

export function Layout({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const fullscreen = FULLSCREEN_PREFIXES.some((prefix) => pathname.startsWith(prefix));

  // Lock the document scroll on the full-screen Stage routes. The <main> below is
  // `h-[100dvh] overflow-hidden`, but on iOS that doesn't stop the *document* from
  // rubber-band overscrolling: the canvas is `position: fixed` so it stays put while
  // the DOM chrome drags up/down a few px under a finger (#162 mobile). There's
  // nothing to scroll here — it's a single edge-to-edge surface — so we pin
  // html/body with overscroll-behavior + overflow hidden while a fullscreen route is
  // mounted, and restore them on the scrolling routes (the landing + the normal
  // max-width pages, which must keep scrolling). overscroll-behavior:none kills the
  // iOS bounce (supported iOS 16+); position:fixed on body is the belt-and-braces
  // stop for the document pan.
  //
  // useLayoutEffect (not useEffect) so the lock + the dark-warm document background are
  // committed SYNCHRONOUSLY during the route's render — i.e. inside the View Transition's
  // DOM-update callback, BEFORE the browser captures the new root snapshot (#346,
  // ADR-0052). A passive effect would apply them after the snapshot, so the warm dissolve
  // would animate a white/unlocked frame that then jumps to the locked studio. This only
  // runs on the fullscreen boundary (`/` ↔ /upload), so /upload ↔ /preview — both
  // fullscreen — never re-runs it and the persistent canvas is untouched.
  useLayoutEffect(() => {
    if (!fullscreen) return;
    const html = document.documentElement;
    const { body } = document;
    const prev = {
      htmlOverflow: html.style.overflow,
      htmlOverscroll: html.style.overscrollBehavior,
      htmlBackground: html.style.background,
      bodyOverflow: body.style.overflow,
      bodyOverscroll: body.style.overscrollBehavior,
      bodyPosition: body.style.position,
      bodyInset: body.style.inset,
      bodyBackground: body.style.background,
    };
    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    // Pin the body to the viewport (canonical iOS scroll lock) so the document can't
    // pan/bounce at all; the fixed Stage canvas + the 100dvh <main> fill it exactly.
    body.style.position = "fixed";
    body.style.inset = "0";
    // Paint the document itself the dark-warm Stage colour while a full-screen route is
    // mounted. The <main> is bg-transparent (the canvas shows through), so without this
    // the white body flashed in the safe-area strips under the notch / home indicator
    // and behind the mobile browser bars — the theme-color meta alone didn't cover it
    // on iOS (Leon, #ui-polish). Restored to paper on the scrolling routes.
    html.style.background = THEME_COLOR.stage;
    body.style.background = THEME_COLOR.stage;
    return () => {
      html.style.overflow = prev.htmlOverflow;
      html.style.overscrollBehavior = prev.htmlOverscroll;
      html.style.background = prev.htmlBackground;
      body.style.overflow = prev.bodyOverflow;
      body.style.overscrollBehavior = prev.bodyOverscroll;
      body.style.position = prev.bodyPosition;
      body.style.inset = prev.bodyInset;
      body.style.background = prev.bodyBackground;
    };
  }, [fullscreen]);

  // Match the mobile browser chrome to the surface: the dark-warm Stage background
  // on the immersive full-screen viewer/upload routes, paper everywhere else. Keeps
  // the strips above/below the viewport from framing the viewer in white (#ui-polish).
  useThemeColor(fullscreen ? THEME_COLOR.stage : THEME_COLOR.paper);

  // The screenshot harness surface: no nav, no footer, no max-width — the route
  // owns the whole viewport so the PNG is the Stage and only the Stage.
  if (BARE_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return <>{children}</>;
  }

  // Routes that bring their OWN body chrome (footer) and paint as light, full-width,
  // crawlable HTML — Layout hands them the viewport but still floats the shared nav on
  // top so the menu + logo are identical everywhere (Leon, #ui-polish). The conversion
  // landing `/` (ADR-0040) and the static comparison page `/comparison` (#164).
  // Deliberately NOT StageHost routes: the 3D canvas stays on /upload + /preview.
  if (pathname === "/" || pathname === "/comparison") {
    return (
      <>
        <StageNav />
        {children}
        {/* Informational analytics notice (ADR-0050): shown on the calm, scrollable
            chrome surfaces — the landing is the funnel's primary entry — and kept off
            the immersive full-screen Stage routes so it never clutters the reveal. */}
        <AnalyticsNotice />
      </>
    );
  }

  if (fullscreen) {
    const stageHost = isStageHostRoute(pathname);
    return (
      <main
        className={`relative h-[100dvh] w-full overflow-hidden text-foreground ${
          stageHost ? "bg-transparent" : "bg-background"
        }`}
      >
        {/* The one persistent Stage canvas, behind everything (z-0). Renders only
            on host routes; survives the studio → /preview navigation unmounted-free
            so the drop → reveal is one continuous scene. */}
        {stageHost ? (
          <Suspense fallback={<StageSurround />}>
            <StageHost />
          </Suspense>
        ) : null}
        {/* One connected floating nav over the viewer (#ui-polish): logo + stitch
            divider + links as a single crafted chrome tag on desktop, collapsing to a
            logo + menu-sheet on a phone. Left-anchored so the hero cushion stays clear. */}
        <StageNav />
        {/* Route chrome over the canvas. On host routes it's pointer-transparent
            (so the canvas behind orbits); each interactive control opts back in. */}
        {stageHost ? (
          <div className="pointer-events-none absolute inset-0 z-10">{children}</div>
        ) : (
          children
        )}
      </main>
    );
  }

  // The account / editor / checkout shells: the SAME floating nav as everywhere else
  // (Leon, #ui-polish), with the content padded clear of it. Footer stays.
  return (
    <div className="flex min-h-svh flex-col bg-background text-foreground">
      <StageNav />
      <main className="mx-auto flex w-full max-w-screen-md flex-1 flex-col px-4 pt-20 pb-6">
        {children}
      </main>
      <SiteFooter />
      {/* Informational analytics notice (ADR-0050) on the standard chrome shells too. */}
      <AnalyticsNotice />
    </div>
  );
}
